/**
 * ConvergencePanel — why distillation terminates and why it is minimal.
 *
 * Plain English first, then notation: well-founded descent on the generator
 * count guarantees termination; the monotone fixed-point distillation reaches
 * the least fixed point (Knaster–Tarski), so no generator is removed
 * unnecessarily.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useMemo } from "react";
import { MathInline, tex, PanelSurface, panelTokens } from "@csk/demo-ui";
import { GENERATOR_SETS } from "../data/generatorSets.js";
import {
  attackSurface,
  distillToFixedPoint,
} from "../algorithms/distillation.js";
export function ConvergencePanel(): React.JSX.Element {
  const sample = GENERATOR_SETS[0];
  const trace = useMemo(
    () => distillToFixedPoint(sample.generators, sample.authoritySubgroup),
    [sample.generators, sample.authoritySubgroup],
  );
  return (
    <PanelSurface.PanelShell
      title="Convergence & Containment"
      subtitle="Termination by well-founded descent on the generator count; minimality by the Knaster–Tarski least fixed point."
      accentColor={panelTokens.immune}
    >
      <PanelSurface.SectionHeading>
        Why it terminates (plain English)
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          Every distillation step that has not yet reached the fixed point
          removes at least one generator. The number of generators is a
          non-negative whole number, and it can only go down — never up — so the
          process cannot run forever. It must stop, and it stops exactly when no
          surviving generator violates any axiom. That stopping point is the
          immune fixed point.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        The descent (live trace)
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.StepList
          ariaLabel="Distillation descent trace"
          items={trace.frames.map((frame) => ({
            id: `step-${String(frame.step)}`,
            title:
              frame.step === 0
                ? `ALLOW₀ · μ = ${String(frame.attackSurface)}`
                : `Step ${String(frame.step)} · μ = ${String(frame.attackSurface)}`,
            color:
              frame.step === trace.steps
                ? panelTokens.immune
                : panelTokens.distill,
            description:
              frame.stripped.length === 0
                ? frame.step === 0
                  ? "Initial generator set the operator supplied."
                  : "Fixed point — every surviving generator is immune."
                : `Stripped ${frame.stripped
                    .map((g) => g.label)
                    .join(", ")} (A1 violators annihilated — exp(X) ∉ H).`,
            meta: `μ ${String(frame.attackSurface)}`,
          }))}
        />
        <PanelSurface.KpiGrid>
          <PanelSurface.KpiBox
            label="initial μ"
            value={String(attackSurface(sample.generators))}
            color={panelTokens.muted}
          />
          <PanelSurface.KpiBox
            label="steps"
            value={String(trace.steps)}
            color={panelTokens.distill}
          />
          <PanelSurface.KpiBox
            label="immune μ"
            value={String(trace.immune.length)}
            color={panelTokens.immune}
          />
        </PanelSurface.KpiGrid>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        The well-founded descent, formally
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Prose className="mx-1 mt-0 mb-3">
          The attack surface{" "}
          <MathInline ariaLabel="mu of ALLOW equals the generator count">
            {tex`\mu(\mathrm{ALLOW}) = |\mathrm{ALLOW}|`}
          </MathInline>{" "}
          is a natural number. Each non-fixed step strictly decreases it, and{" "}
          <MathInline>{tex`(\mathbb{N}, <)`}</MathInline> is well-founded, so
          the accessibility predicate{" "}
          <MathInline>{tex`\mathrm{Acc}\;\_{<}\_`}</MathInline> bottoms out.
        </PanelSurface.Prose>
        <PanelSurface.Mono>{`-- Termination witnessed by Acc _<_ on generator count
-- (Data.Nat.Induction.<-wellFounded)
distillation-converges :
  ∀ (ALLOW₀ : SquareSet) → Σ ℕ λ k → distillᵏ ALLOW₀ ≡ distillᵏ⁺¹ ALLOW₀
-- count-decreases : μ (distill A) < μ A   whenever A is not a fixed point
-- empty-implies-fixed : μ A ≡ 0  ⇒  distill A ≡ A`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>
        Minimality — the greatest immune subset
      </PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Prose className="mx-1 mt-0 mb-3">
          Distillation is{" "}
          <strong className="text-[var(--vid-immune)]">monotone</strong> and{" "}
          <strong className="text-[var(--vid-immune)]">decreasing</strong> on
          the inclusion lattice of generator subsets (condition C7): it removes
          only generators that genuinely violate an axiom and never adds one. So
          the fixed point{" "}
          <MathInline ariaLabel="ALLOW star is contained in ALLOW zero">
            {tex`\mathrm{ALLOW}^{*} \subseteq \mathrm{ALLOW}_{0}`}
          </MathInline>{" "}
          is the{" "}
          <strong className="text-[var(--vid-immune)]">
            greatest immune subset
          </strong>{" "}
          of the initial set — every generator it keeps is compliant and every
          one it drops genuinely violates an axiom, so no generator is removed
          unnecessarily. RD-65 records the dual extremal property as a
          containment lemma:{" "}
          <MathInline ariaLabel="ALLOW star is contained in ALLOW prime">
            {tex`\mathrm{ALLOW}^{*} \subseteq \mathrm{ALLOW}'`}
          </MathInline>{" "}
          for every immune (fixed-point) set{" "}
          <MathInline>{tex`\mathrm{ALLOW}' \supseteq \mathrm{ALLOW}_{0}`}</MathInline>{" "}
          that still contains the initial set.
        </PanelSurface.Prose>
        <PanelSurface.Mono>{`-- DistillationContainment.immune-contained
immune-contained :
  ∀ (ALLOW′ : SquareSet) → ALLOW₀ ⊆ ALLOW′ → distill ALLOW′ ≡ ALLOW′
  → IMMUNE ALLOW₀ ⊆ ALLOW′
-- proved by well-founded induction on step count k`}</PanelSurface.Mono>
      </PanelSurface.Card>

      <PanelSurface.Callout
        title="Confluence — order does not matter"
        tone="safe"
        color={panelTokens.immune}
      >
        <PanelSurface.Prose className="m-0">
          A1 (<MathInline>{tex`\exp(X) \in H`}</MathInline>) is the sole
          violable predicate, evaluated independently per generator — A2, A3,
          and A4 hold for every gl(n,ℂ) generator by construction, so they never
          gate survival. A generator therefore survives iff its group image
          lands in the operator's authority subgroup H, independent of the order
          in which violators are stripped. Whichever violator the lab removes
          first, the fixed point is the same immune subset (RD-65 open question
          2, resolved affirmatively for the deterministic monotone
          distillation).
        </PanelSurface.Prose>
      </PanelSurface.Callout>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label: "Abstract ASCT (convergence)",
            path: "agda/AttackSurfaceConvergence/Convergence.agda",
          },
          {
            label: "Containment theorem (Leastness)",
            path: "agda/AttackSurfaceConvergence/Leastness.agda",
          },
          {
            label: "Attack Surface Convergence",
            path: "theory/structural/ATTACK-SURFACE-CONVERGENCE.md",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
